/**
 * Planillas Excel gratuitas de /recursos (lead magnets SEO: compiten con
 * Scribd en "planilla ganadera excel" y cierran con "importala a la app").
 *
 * Los archivos salen de scripts/build-planillas.mjs (public/descargas). Las
 * preguntas frecuentes de cada planilla viven en src/content/faq/<slug>.json
 * (misma fuente para la página y el FAQPage del prerender). Title y
 * description, en src/content/seoPaginas.json bajo `seoKey`.
 */

export interface HojaPlanilla {
  nombre: string;
  texto: string;
}

export interface RecursoPlanilla {
  slug: string;
  /** Clave en seoPaginas.json. */
  seoKey: 'planillaGanadera' | 'planillaExistencias' | 'planillaFeedlot';
  archivo: string;
  /** H1 visible. */
  titulo: string;
  /** Nombre corto para tarjetas y migas. */
  nombreCorto: string;
  resumen: string;
  /** Primer párrafo: responde directo qué es y para qué sirve. */
  intro: string;
  hojas: HojaPlanilla[];
  pasos: string[];
  /** Qué cambia si lo llevás en la app. */
  enLaApp: string[];
}

export const RECURSOS: RecursoPlanilla[] = [
  {
    slug: 'planilla-ganadera',
    seoKey: 'planillaGanadera',
    archivo: '/descargas/planilla-ganadera.xlsx',
    titulo: 'Planilla ganadera en Excel gratis: registro de ganado por caravana',
    nombreCorto: 'Planilla ganadera',
    resumen: 'Registro del rodeo por caravana, pesadas con ganancia diaria, sanidad y resumen por categoría.',
    intro:
      'Esta planilla ganadera en Excel es gratis y sirve para llevar el registro de ganado animal por animal: caravana, categoría, sexo, raza, edad, peso, preñez y estado, más las pesadas y la sanidad del rodeo. Está armada con las columnas que usa un productor en la manga y se importa tal cual a Mi Campo Digital cuando quieras dejar el Excel.',
    hojas: [
      {
        nombre: 'Rodeo',
        texto:
          'Un animal por fila: caravana, fecha del dato, categoría, sexo, raza, edad por dentición, peso, preñez, si tiene ternero al pie, estado y observaciones. Las columnas tienen listas desplegables para no escribir distinto cada vez.',
      },
      {
        nombre: 'Pesadas',
        texto:
          'Hasta tres pesadas por animal, cada una con su fecha (Fecha 1 y Peso 1, Fecha 2 y Peso 2, Fecha 3 y Peso 3). Los días, el aumento y la ganancia diaria (GDP) se calculan solos.',
      },
      {
        nombre: 'Sanidad',
        texto:
          'Vacunas, antiparasitarios y tratamientos por animal o por lote, con el producto, la dosis, las cabezas y la fecha de la próxima aplicación.',
      },
      {
        nombre: 'Resumen',
        texto: 'Cabezas activas y peso promedio por categoría, calculados desde la hoja Rodeo.',
      },
    ],
    pasos: [
      'Descargá la planilla y abrila en Excel, Google Sheets o LibreOffice.',
      'Borrá las filas de ejemplo de las hojas Rodeo y Pesadas.',
      'Cargá un animal por fila con su caravana. Si todavía no tiene caravana electrónica, usá el número de la visual.',
      'Cada vez que pesás, anotá la fecha y el peso en la hoja Pesadas para ver la ganancia diaria.',
      'Mirá el Resumen para saber cuántas cabezas activas tenés de cada categoría.',
    ],
    enLaApp: [
      'Importás esta misma planilla en Mi Ganado: la app reconoce las hojas Rodeo y Pesadas, y cada pesada entra con su fecha en la historia del animal.',
      'La caravana entra con el lector y el peso con la balanza, sin tipear, aun sin señal.',
      'Cada pesada queda en la historia del animal y la ganancia diaria se calcula sola, por animal y por lote.',
      'Con los mismos datos armás la declaración del SIGSA y ves cuánto vale tu rodeo con el precio del día.',
    ],
  },
  {
    slug: 'planilla-existencias',
    seoKey: 'planillaExistencias',
    archivo: '/descargas/planilla-existencias-ganaderas.xlsx',
    titulo: 'Planilla de existencia ganadera en Excel: stock por categoría',
    nombreCorto: 'Planilla de existencias',
    resumen:
      'Stock inicial, nacimientos, compras, ventas, muertes y cambios de categoría, con el stock final calculado.',
    intro:
      'La planilla de existencia ganadera te dice cuántas cabezas tenés de cada categoría al final de un período: partís del stock inicial y sumás o restás cada nacimiento, compra, venta, muerte y cambio de categoría. Es gratis, está en Excel y el stock final se calcula solo.',
    hojas: [
      {
        nombre: 'Movimientos',
        texto:
          'Cada movimiento con su fecha, tipo (nacimiento, compra, venta, muerte o cambio de categoría), categoría y cabezas.',
      },
      {
        nombre: 'Existencias',
        texto:
          'Cargás el stock inicial y el período (desde y hasta). Por categoría, la planilla suma los movimientos del período y calcula el stock final y el total del campo.',
      },
    ],
    pasos: [
      'Descargá la planilla y borrá los movimientos de ejemplo.',
      'En la hoja Existencias, poné las fechas del período y el stock inicial de cada categoría (celdas color crema).',
      'Anotá cada movimiento en la hoja Movimientos el día que pasa.',
      'Cuando un ternero pasa a novillo o una vaquillona a vaca, cargá una fila "Cambio (sale)" en la categoría vieja y otra "Cambio (entra)" en la nueva.',
      'Compará el stock final con el último conteo en la manga o la última vacunación: si no coincide, falta algún movimiento.',
    ],
    enLaApp: [
      'Las existencias salen solas de tus animales, por categoría y por potrero.',
      'Ventas, compras y muertes se cargan en segundos, aun sin elegir caravanas.',
      'Ves los movimientos de tu campo en un vistazo y de qué lote a qué lote se movió cada animal.',
    ],
  },
  {
    slug: 'planilla-feedlot',
    seoKey: 'planillaFeedlot',
    archivo: '/descargas/planilla-feedlot.xlsx',
    titulo: 'Planilla de feedlot en Excel: costo por kilo producido y margen por lote',
    nombreCorto: 'Planilla de feedlot',
    resumen: 'Lotes de engorde a corral, comida entregada, conversión, costo por kilo producido y margen.',
    intro:
      'Esta planilla de feedlot en Excel sigue cada lote de engorde a corral desde que entra hasta que sale: días, ganancia diaria, kilos producidos, comida entregada, conversión, costo por kilo producido y margen del lote. Es gratis y las cuentas se hacen solas.',
    hojas: [
      {
        nombre: 'Lotes',
        texto:
          'Una fila por lote con el corral, la fecha y el peso de entrada, el precio de compra, la fecha y el peso de salida, el precio de venta y otros costos. Calcula los días, la ganancia diaria y los kilos producidos.',
      },
      {
        nombre: 'Alimentación',
        texto:
          'Cada entrega de comida al lote en kilos tal cual, con su precio por kilo. El costo de cada entrega se calcula solo.',
      },
      {
        nombre: 'Resultado',
        texto:
          'Por lote: kilos de alimento, costo de alimentación, conversión (kilos de alimento por kilo producido), costo por kilo producido, compra, venta y margen.',
      },
    ],
    pasos: [
      'Descargá la planilla y borrá el lote de ejemplo.',
      'Cargá cada lote en la hoja Lotes cuando entra al corral.',
      'Anotá la comida que le entregás a cada lote en la hoja Alimentación, con el mismo nombre de lote.',
      'Cuando el lote sale, completá la fecha, el peso y el precio de venta.',
      'Mirá la hoja Resultado: el costo por kilo producido te dice si el engorde te deja plata.',
    ],
    enLaApp: [
      'Los corrales y sus animales están en el mapa del campo, con el peso promedio real de cada corral.',
      'Las pesadas entran con la balanza y la ganancia diaria se calcula por animal y por corral.',
      'La calculadora de ración usa tus precios reales de insumos y la de engorde, el precio promedio de remates.',
    ],
  },
];

export const recursoPorSlug = (slug: string | undefined): RecursoPlanilla | undefined =>
  RECURSOS.find((r) => r.slug === slug);
